import { IntlShape, defineMessages } from 'react-intl'
import * as yup from 'yup'

import { IPartnerContact } from 'features/Partners/types'
import schemaValidations from 'utils/schema'
import { isEmpty } from 'lodash'

const messages = defineMessages({
  required: {
    id: 'partnerData.new.form.required',
    defaultMessage: 'This field is required'
  },
  max: {
    id: 'partnerData.new.form.max',
    defaultMessage: 'This field must be at most {max} characters'
  },
  email: {
    id: 'partnerData.new.form.email',
    defaultMessage: 'This field must be a valid email'
  }
})

const base = (intl: IntlShape): yup.SchemaOf<IPartnerContact> => {
  return yup.object().shape({
    id: yup.string(),
    name: yup.string().max(128, intl.formatMessage(messages.max, { max: 128 })),
    phone: schemaValidations(intl).phone.optional,
    secondaryPhone: schemaValidations(intl).phone.optional,
    email: schemaValidations(intl).email.optional,
    secondaryEmail: schemaValidations(intl).email.optional
  }) as yup.SchemaOf<IPartnerContact>
}

const required = (intl: IntlShape): yup.SchemaOf<IPartnerContact> => {
  return yup.object().shape({
    id: yup.string(),
    name: base(intl).fields.name.required(intl.formatMessage(messages.required)),
    phone: base(intl).fields.phone.required(intl.formatMessage(messages.required)),
    email: base(intl).fields.email.required(intl.formatMessage(messages.required)),
    secondaryPhone: base(intl).fields.secondaryPhone.optional(),
    secondaryEmail: base(intl).fields.secondaryEmail.optional()
  })
}

const optional = (intl: IntlShape): yup.SchemaOf<IPartnerContact> => {
  return yup.object().shape(
    {
      id: yup.string(),
      name: base(intl).fields.name.when(['phone', 'email'], {
        is: (phone: string, email: string) => !isEmpty(phone) || !isEmpty(email),
        then: base(intl).fields.name.required(intl.formatMessage(messages.required))
      }),
      phone: base(intl).fields.phone.when(['name', 'email'], {
        is: (name: string, email: string) => !isEmpty(name) || !isEmpty(email),
        then: base(intl).fields.phone.required(intl.formatMessage(messages.required))
      }),
      secondaryPhone: base(intl).fields.secondaryPhone,
      email: base(intl).fields.email.when(['name', 'phone'], {
        is: (name: string, phone: string) => !isEmpty(name) || !isEmpty(phone),
        then: base(intl).fields.email.required(intl.formatMessage(messages.required))
      }),
      secondaryEmail: base(intl).fields.secondaryEmail
    },
    [
      ['phone', 'name'],
      ['email', 'name'],
      ['phone', 'email']
    ]
  ) as yup.SchemaOf<IPartnerContact>
}

export const partnerContactSchema = {
  required,
  optional
}
